import dayjs from "dayjs";
import type {
  LeaveRequest,
  LeaveRequestSortBy,
  LeaveRequestsQuery,
  LeaveType,
} from "../api/leave/Leave.typed";
import { LeaveStatus } from "../api/leave/Leave.typed";
import {
  formatDateWithLocale,
  getTodayDateStringInTimeZone,
  ymdToRFC3339InTimeZone,
} from "./dateTime";

/** Congés / RH: permissions, queries, filters, dates, status chips for validation lists. Planning-specific helpers live in `utils/planning/planningLeaveHelpers`. */

// Local UI mapping (API exposes numeric codes; labels/colors are front-only)
export const getStatusChip = (status: number) => {
  switch (status) {
    case LeaveStatus.PENDING:
      return { id: status, name: "En attente", color: "warning" } as const;
    case LeaveStatus.APPROVED:
      return { id: status, name: "Approuvé", color: "success" } as const;
    case LeaveStatus.REJECTED:
      return { id: status, name: "Refusé", color: "danger" } as const;
    case LeaveStatus.CANCELLED:
      return { id: status, name: "Annulé", color: "neutral" } as const;
    case LeaveStatus.CANCELLATION_REQUESTED:
      return { id: status, name: "Demande d'annulation", color: "warning" } as const;
    default:
      return null;
  }
};

export const getTypeName = (typeId: number, typesMap?: Record<number, LeaveType>) => {
  if (typesMap && typesMap[typeId]) return typesMap[typeId].Name;
  return "Inconnu";
};

/** Global catalog row (`company_id` null in DB); not tenant-owned. */
export function isPlatformLeaveType(t: Pick<LeaveType, "CompanyID">): boolean {
  return t.CompanyID == null;
}

export const formatDate = (dateString: string) => {
  return formatDateWithLocale(dateString);
};

export const formatDateRange = (startDate: string, endDate: string) => {
  return `${formatDateWithLocale(startDate)} - ${formatDateWithLocale(endDate)}`;
};

const MINUTES_PER_DAY = 24 * 60;

/**
 * Human-readable period for a leave request. `start_at` / `end_at` are treated as wall clock
 * encoded as UTC (TZ-agnostic for leaves), so we read UTC components without `.tz()` conversion.
 * Falls back to the calendar `start_date` / `end_date` range when at/end are absent.
 */
export function formatLeavePeriod(
  request: Pick<LeaveRequest, "start_date" | "end_date" | "start_at" | "end_at">,
  _ianaTimeZone?: string | null,
): string {
  if (!request.start_at || !request.end_at) {
    return formatDateRange(request.start_date, request.end_date);
  }

  const start = dayjs.utc(request.start_at);
  const end = dayjs.utc(request.end_at);

  if (!start.isValid() || !end.isValid()) {
    return formatDateRange(request.start_date, request.end_date);
  }

  const diffMin = Math.max(0, Math.round((end.valueOf() - start.valueOf()) / 60000));
  // Full-day convention: `N*24h - 1min` (e.g. 00:00 → 23:59 over N days) rolls up to N whole days.
  const isFullDayPattern = diffMin > 0 && (diffMin + 1) % MINUTES_PER_DAY === 0;
  const sameDay = start.isSame(end, "day");

  if (isFullDayPattern) {
    if (sameDay) {
      return start.format("DD/MM/YYYY");
    }

    return `${start.format("DD/MM/YYYY")} → ${end.format("DD/MM/YYYY")}`;
  }

  const startHm = start.format("HH:mm");
  const endHm = end.format("HH:mm");

  if (sameDay) {
    return `${start.format("DD/MM/YYYY")} · ${startHm} – ${endHm}`;
  }

  return `${start.format("DD/MM/YYYY")} ${startHm} - ${end.format("DD/MM/YYYY")} ${endHm}`;
}

/**
 * Human-readable duration regardless of the type unit: day-unit falls back to `{days}j`,
 * hourly < 24h renders as `Xh` / `XhMM`, hourly ≥ 24h as `Nj` / `Nj Yh`. A `N*24h - 1min`
 * residue rolls up to N whole days (full-day convention 00:00 → 23:59).
 */
export function formatLeaveDurationLabel(
  request: Pick<LeaveRequest, "start_at" | "end_at" | "days">,
  _ianaTimeZone?: string | null,
): string {
  if (!request.start_at || !request.end_at) {
    return request.days > 0 ? `${request.days}j` : "—";
  }

  const startMs = new Date(request.start_at).getTime();
  const endMs = new Date(request.end_at).getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return request.days > 0 ? `${request.days}j` : "—";
  }

  const diffMin = Math.max(0, Math.round((endMs - startMs) / 60000));

  if (diffMin === 0) {
    return "—";
  }

  // Full-day convention: `N*24h - 1min` (e.g. 00:00 → 23:59 over N days) rolls up to N whole days.
  const adjustedDiff = (diffMin + 1) % MINUTES_PER_DAY === 0 ? diffMin + 1 : diffMin;

  if (adjustedDiff < MINUTES_PER_DAY) {
    const hours = Math.floor(adjustedDiff / 60);
    const minutes = adjustedDiff % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h${minutes.toString().padStart(2, "0")}`;
  }

  const days = Math.floor(adjustedDiff / MINUTES_PER_DAY);
  const remainingMin = adjustedDiff % MINUTES_PER_DAY;

  if (remainingMin === 0) {
    return `${days}j`;
  }

  const remHours = Math.floor(remainingMin / 60);
  const remMinutes = remainingMin % 60;

  if (remMinutes === 0) {
    return `${days}j ${remHours}h`;
  }

  return `${days}j ${remHours}h${remMinutes.toString().padStart(2, "0")}`;
}

export const canEditDelete = (request: LeaveRequest, userId?: number) => {
  return request.status === LeaveStatus.PENDING && request.user_id === userId;
};

export const canCancel = (request: LeaveRequest, userId?: number) => {
  return request.status === LeaveStatus.APPROVED && request.user_id === userId;
};

export const canApproveReject = (request: LeaveRequest) => {
  return (
    request.status === LeaveStatus.PENDING || request.status === LeaveStatus.CANCELLATION_REQUESTED
  );
};

export const canDelete = (request: LeaveRequest) => {
  return (
    request.status === LeaveStatus.PENDING ||
    request.status === LeaveStatus.APPROVED ||
    request.status === LeaveStatus.CANCELLATION_REQUESTED
  );
};

export const getPendingCount = (requests: LeaveRequest[]) => {
  if (!requests || !Array.isArray(requests)) {
    return 0;
  }
  return requests.filter((r) => r.status === LeaveStatus.PENDING).length;
};

export const filterRequestsBySearch = (requests: LeaveRequest[], searchTerm: string) => {
  if (!requests || !Array.isArray(requests)) {
    return [];
  }
  if (!searchTerm.trim()) return requests;

  return requests.filter((request) =>
    (`${request.user_id}` || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );
};

export const buildLeaveQuery = (params: {
  page: number;
  limit: number;
  statusFilter?: number;
  typeFilter?: number;
  userId?: number;
  startDate?: string;
  endDate?: string;
  sortBy?: LeaveRequestSortBy;
}): LeaveRequestsQuery => {
  const query: LeaveRequestsQuery = {};

  if (params.page !== undefined) query.Page = params.page;
  if (params.limit !== undefined) query.Limit = params.limit;

  if (params.statusFilter !== undefined && params.statusFilter !== -1) {
    query.Status = params.statusFilter;
  }
  if (params.typeFilter !== undefined && params.typeFilter !== -1) {
    (query as LeaveRequestsQuery & { TypeID?: number }).TypeID = params.typeFilter;
  }
  if (params.userId) {
    query.UserID = params.userId;
  }
  if (params.startDate) {
    query.StartDate = params.startDate;
  }
  if (params.endDate) {
    query.EndDate = params.endDate;
  }
  if (params.sortBy) {
    query.SortBy = params.sortBy;
  }

  return query;
};

export const calculateTotalRequests = (dataLength: number, limit: number, page: number) => {
  return dataLength === limit ? page * limit + 1 : page * limit;
};

export const getDefaultDateRange = (ianaTimeZone?: string | null) => {
  const y = parseInt(getTodayDateStringInTimeZone(ianaTimeZone).slice(0, 4), 10);
  const from = `${y}-01-01`;
  const to = `${y}-12-31`;
  return {
    startDate: ymdToRFC3339InTimeZone(from, ianaTimeZone),
    endDate: ymdToRFC3339InTimeZone(to, ianaTimeZone),
  };
};
